import {useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Checkbox} from '@/components/ui/checkbox'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Hint} from '@/components/ui/hint'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {HelpIcon} from '@/components/help-icon'
import {
  INFORMES_EXTERNOS_PAGE_SIZE,
  mintInformeExternoUploadTarget,
  useEliminarInformeExterno,
  useInformesExternos,
  useRegistrarInformeExterno,
  type BateriaConsolidadoTenant,
  type BateriaInformeExterno,
  type InformeExternoCreate,
} from '@/hooks/bateria/use-bateria-solicitudes'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const NIVELES = ['sin', 'bajo', 'medio', 'alto', 'muy_alto'] as const
type NivelOpcion = (typeof NIVELES)[number]

/** La casilla del estándar 4.1.2 nace apagada: colgar el informe de un estándar lo decide la empresa. */
function SubirInformeDialog({businessId, onClose}: {businessId: string; onClose: () => void}) {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const registrar = useRegistrarInformeExterno(businessId)
  const [file, setFile] = useState<File | null>(null)
  const [fecha, setFecha] = useState('')
  const [proveedor, setProveedor] = useState('')
  const [nivel, setNivel] = useState<NivelOpcion>('sin')
  const [notas, setNotas] = useState('')
  const [soporte412, setSoporte412] = useState(false)
  const [errors, setErrors] = useState<{
    file?: string
    fecha?: string
    proveedor?: string
  }>({})
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  function validateProveedor(): string | undefined {
    return validateRequired(proveedor) ? 'bateria.informeExterno.proveedorRequerido' : undefined
  }

  function validateForm(): boolean {
    const next = {
      file: file ? undefined : 'bateria.informeExterno.archivoRequerido',
      fecha: validateRequired(fecha) ? 'bateria.informeExterno.fechaRequerida' : undefined,
      proveedor: validateProveedor(),
    }
    setErrors(next)
    return !next.file && !next.fecha && !next.proveedor
  }

  function pick(files: FileList | null) {
    const picked = files?.[0] ?? null
    setError(null)
    if (picked && !EVIDENCE_ALLOWED_TYPES.has(picked.type)) return setError(t('errors.evidenceBadFormat'))
    if (picked && picked.size > EVIDENCE_MAX_BYTES) return setError(evidenceTooBig(t, picked))
    setFile(picked)
    setErrors((e) => ({...e, file: undefined}))
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (!validateForm() || !file) return
    setError(null)
    try {
      setProgress(0)
      const target = await mintInformeExternoUploadTarget(businessId, file)
      await uploadToStorage(target, file, setProgress)
      const body: InformeExternoCreate = {
        object_key: target.object_key,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        fecha_aplicacion: fecha,
        proveedor: proveedor.trim(),
        nivel_global: nivel === 'sin' ? null : nivel,
        notas: notas.trim() || null,
        usar_como_soporte_412: soporte412,
      }
      await registrar.mutateAsync(body)
      onClose()
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="informe-externo-dialog">
        <DialogHeader>
          <DialogTitle>{t('bateria.informeExterno.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label>{t('bateria.informeExterno.archivo')}</Label>
            <input
              ref={inputRef}
              type="file"
              accept={EVIDENCE_ACCEPT}
              hidden
              data-testid="informe-externo-input"
              onChange={(e) => pick(e.target.files)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid="informe-externo-elegir"
                onClick={() => inputRef.current?.click()}>
                {t('bateria.informeExterno.elegir')}
              </Button>
              {file && (
                <span className="font-mono text-xs text-muted-foreground" data-testid="informe-externo-picked">
                  {file.name}
                </span>
              )}
            </div>
            <FieldError error={errors.file} />
          </div>
          <div className="space-y-2">
            <DateField
              id="informe-externo-fecha"
              label={t('bateria.informeExterno.fecha')}
              value={fecha}
              onChange={(v) => {
                setFecha(v)
                setErrors((prev) => ({...prev, fecha: undefined}))
              }}
            />
            <FieldError error={errors.fecha} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="informe-externo-proveedor">{t('bateria.informeExterno.proveedor')}</Label>
            <Input
              id="informe-externo-proveedor"
              data-testid="informe-externo-proveedor"
              value={proveedor}
              maxLength={tope('RAZ')}
              onChange={(e) => setProveedor(e.target.value)}
              onBlur={() =>
                setErrors((prev) => ({
                  ...prev,
                  proveedor: validateProveedor(),
                }))
              }
              aria-invalid={!!errors.proveedor}
            />
            <FieldError error={errors.proveedor} />
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Label htmlFor="informe-externo-nivel">{t('bateria.informeExterno.nivel')}</Label>
              <HelpIcon
                label={t('common.help.iconLabelFor', {
                  campo: t('bateria.informeExterno.nivel'),
                })}
                testId="informe-externo-nivel-help">
                {t('bateria.informeExterno.nivelHelp')}
              </HelpIcon>
            </div>
            <select
              id="informe-externo-nivel"
              data-testid="informe-externo-nivel"
              value={nivel}
              onChange={(e) => setNivel(e.target.value as NivelOpcion)}
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none">
              {NIVELES.map((n) => (
                <option key={n} value={n}>
                  {t(`bateria.informeExterno.nivelOpciones.${n}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="informe-externo-notas">{t('bateria.informeExterno.notas')}</Label>
            <Textarea
              id="informe-externo-notas"
              data-testid="informe-externo-notas"
              value={notas}
              maxLength={1000}
              minRows={2}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="informe-externo-soporte-412"
              data-testid="informe-externo-soporte-412"
              checked={soporte412}
              onCheckedChange={setSoporte412}
            />
            <div className="space-y-0.5">
              <Label htmlFor="informe-externo-soporte-412">{t('bateria.informeExterno.soporte412')}</Label>
              <Hint>{t('bateria.informeExterno.soporte412Help')}</Hint>
            </div>
          </div>
          {progress !== null && (
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-secondary"
              data-testid="informe-externo-progreso">
              <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={progress !== null || registrar.isPending}
            submitLabel={t('bateria.informeExterno.submit')}
            onCancel={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}

function InformeFila({
  informe,
  vigente,
  canEdit,
  onEliminar,
}: {
  informe: BateriaInformeExterno
  vigente: boolean
  canEdit: boolean
  onEliminar: (informe: BateriaInformeExterno) => void
}) {
  const {t} = useTranslation()
  return (
    <li className="flex flex-wrap items-center gap-2 py-2 text-sm" data-testid={`informe-externo-fila-${informe.id}`}>
      <span className="font-mono text-xs text-muted-foreground">{formatDateOnly(informe.fecha_aplicacion)}</span>
      <span className="text-foreground">{informe.proveedor}</span>
      {vigente && <Tag tone="green">{t('bateria.informeExterno.chip')}</Tag>}
      {informe.soporte_412 && <Tag tone="neutral">4.1.2</Tag>}
      <a
        href={informe.download_url}
        target="_blank"
        rel="noreferrer"
        className="text-primary underline-offset-2 hover:underline"
        data-testid={`informe-externo-descargar-${informe.id}`}>
        {t('bateria.informeExterno.descargar')}
      </a>
      {canEdit && (
        <Button
          size="sm"
          variant="ghost"
          data-testid={`informe-externo-eliminar-${informe.id}`}
          onClick={() => onEliminar(informe)}>
          {t('bateria.informeExterno.eliminar')}
        </Button>
      )}
    </li>
  )
}

export function InformeExternoSection({
  businessId,
  canEdit,
  consolidado,
}: {
  businessId: string
  canEdit: boolean
  consolidado: BateriaConsolidadoTenant | undefined
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const [abierto, setAbierto] = useState(false)
  const [aEliminar, setAEliminar] = useState<BateriaInformeExterno | null>(null)
  const informes = useInformesExternos(businessId, page, true)
  const eliminar = useEliminarInformeExterno(businessId)
  const vigenteId = consolidado?.origen === 'externo' ? consolidado.informe_externo_id : null

  return (
    <div className="space-y-3" data-testid="bateria-informe-externo">
      {consolidado?.origen === 'externo' && (
        <div className="flex flex-wrap items-center gap-2" data-testid="bateria-informe-externo-vigente">
          <Tag tone="green">{t('bateria.informeExterno.chip')}</Tag>
          <span className="text-sm text-foreground">
            {t('bateria.informeExterno.vigente', {
              proveedor: consolidado.proveedor,
              fecha: consolidado.fecha_aplicacion ? formatDateOnly(consolidado.fecha_aplicacion) : '',
            })}
          </span>
        </div>
      )}
      {canEdit && (
        <Button size="sm" variant="outline" data-testid="informe-externo-subir" onClick={() => setAbierto(true)}>
          {t('bateria.informeExterno.subir')}
        </Button>
      )}
      {informes.data && informes.data.total > 0 && (
        <div data-testid="informe-externo-historial">
          <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
            {t('bateria.informeExterno.historial')}
          </p>
          <ul className="divide-y divide-border/60">
            {informes.data.items.map((informe) => (
              <InformeFila
                key={informe.id}
                informe={informe}
                vigente={informe.id === vigenteId}
                canEdit={canEdit}
                onEliminar={setAEliminar}
              />
            ))}
          </ul>
          {informes.data.total > INFORMES_EXTERNOS_PAGE_SIZE && (
            <PaginationBar
              page={page}
              pageSize={INFORMES_EXTERNOS_PAGE_SIZE}
              total={informes.data.total}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
      {abierto && <SubirInformeDialog businessId={businessId} onClose={() => setAbierto(false)} />}
      <ConfirmDialog
        open={aEliminar !== null}
        onOpenChange={(open) => !open && setAEliminar(null)}
        title={t('bateria.informeExterno.eliminarTitulo')}
        description={t('bateria.informeExterno.eliminarCuerpo')}
        confirmLabel={t('bateria.informeExterno.eliminar')}
        cancelLabel={t('common.cancel')}
        pending={eliminar.isPending}
        onConfirm={() => {
          if (!aEliminar) return
          eliminar.mutate(aEliminar.id, {
            onSuccess: () => setAEliminar(null),
          })
        }}
      />
    </div>
  )
}
